import * as React from "react";
import { ShieldCheck } from "lucide-react";
import { useAuth } from "@/auth/AuthProvider";
import { NEW_PASSWORD_HINT, PASSWORDS_DIFFER } from "@/auth/password-rules";
import { Button } from "@/components/ui/button";
import { Field, Input } from "@/components/ui/input";
import { Notice } from "@/components/ui/feedback";

/**
 * The only screen reachable without a session, and it asks ONE question.
 *
 * THERE IS NO EMAIL FIELD, and that is the design rather than an omission.
 * Cloudflare Access has already proved who is at the door; the account is
 * whoever it asserted. A caller cannot try a password against somebody else's
 * account because there is nowhere to name them.
 *
 * What the screen offers depends on the account, and `/auth/state` answers that
 * in one call so the app never has to guess from a 401:
 *
 *   mayBootstrap -> the shop has no administrator yet and this is the address
 *                   `ADMIN_BOOTSTRAP_EMAIL` names. Choose the first password.
 *   !known       -> Access let them through but nobody has invited them. There
 *                   is nothing they can do here, and saying so is kinder than
 *                   a password box that will always be wrong.
 *   suspended    -> likewise, and for a reason a colleague will know.
 *   !claimed     -> invited, or the password was revoked. Choose one.
 *   otherwise    -> sign in.
 */
export function SignInScreen() {
  const { state, error, signIn, claimAccount } = useAuth();
  const [password, setPassword] = React.useState("");
  const [confirm, setConfirm] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [failure, setFailure] = React.useState<string | null>(null);

  const setting = state !== null && !state.claimed && (state.known || state.mayBootstrap);
  const blocked = state !== null && !state.known && !state.mayBootstrap;
  const suspended = state?.suspended === true;

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setFailure(null);
    // `password-rules.ts` says why only a browser checks this.
    if (setting && password !== confirm) {
      setFailure(PASSWORDS_DIFFER);
      return;
    }
    setBusy(true);
    try {
      if (setting) await claimAccount(password);
      else await signIn(password);
    } catch (err) {
      setFailure(err instanceof Error ? err.message : "That did not work");
      setPassword("");
      setConfirm("");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-full items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex items-center gap-3">
          <span className="grid h-10 w-10 place-items-center rounded-lg bg-primary-strong text-base font-bold text-primary-foreground">
            e
          </span>
          <div>
            <div className="font-semibold tracking-tight">eSIM with Number</div>
            <div className="text-xs text-muted-foreground">Administration</div>
          </div>
        </div>

        <div className="rounded-lg border border-border bg-card p-5 shadow-card">
          {/* Access is the outer door and it has already opened. Naming the
              address it asserted is what lets somebody notice they are on a
              colleague's laptop BEFORE they set a password on that account. */}
          {state && (
            <div className="mb-4 flex items-start gap-2 rounded border border-border-light bg-muted px-3 py-2 text-xs text-secondary">
              <ShieldCheck aria-hidden className="mt-0.5 h-3.5 w-3.5 shrink-0 text-success" />
              <span className="min-w-0">
                Cloudflare Access signed you in as <span className="font-medium text-foreground">{state.email}</span>
              </span>
            </div>
          )}

          {error && (
            <Notice tone="error" title="Could not reach the API">
              {error}
            </Notice>
          )}

          {suspended && (
            <Notice tone="error" title="This account is suspended">
              Another administrator has to reinstate it before you can sign in.
            </Notice>
          )}

          {!suspended && blocked && (
            <Notice tone="warning" title="No administrator account">
              You are past Cloudflare Access, but nobody has invited this address yet. An existing administrator can add
              it on the Administrators screen.
            </Notice>
          )}

          {!suspended && !blocked && state && (
            <form onSubmit={submit} className="space-y-4">
              {setting && (
                <p className="text-sm text-secondary">
                  {state.mayBootstrap
                    ? "Nobody administers this shop yet. Choose the first password."
                    : "This account has no password yet. Choose one."}
                </p>
              )}

              <Field
                label="Password"
                htmlFor="password"
                hint={setting ? NEW_PASSWORD_HINT : undefined}
              >
                <Input
                  id="password"
                  type="password"
                  // `new-password` on the set flows stops a password manager
                  // offering the OLD one for a box that must not take it.
                  autoComplete={setting ? "new-password" : "current-password"}
                  autoFocus
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  aria-invalid={failure !== null}
                />
              </Field>

              {setting && (
                <Field label="Password again" htmlFor="confirm">
                  <Input
                    id="confirm"
                    type="password"
                    autoComplete="new-password"
                    required
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    aria-invalid={failure !== null}
                  />
                </Field>
              )}

              {failure && <Notice tone="error">{failure}</Notice>}

              <Button type="submit" className="w-full" disabled={busy || password.length === 0}>
                {busy ? "Working…" : setting ? "Set password and sign in" : "Sign in"}
              </Button>
            </form>
          )}
        </div>

        {/* Said once, at the bottom, where somebody looking for it will find
            it -- and not on the button, where it would be advice about a thing
            they cannot do from here. */}
        <p className="mt-4 text-center text-xs text-muted-foreground">
          Forgotten it? Another administrator can revoke the password, and you choose a new one here.
        </p>
      </div>
    </div>
  );
}
